import { HttpErrorResponse } from '@angular/common/http';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe';
import {
    IAnnualReport,
    IAnnualReportPdfRequest,
    IFinStatements,
    IFinancialStatementSource,
    IPrintOptions,
    ISetupData,
    ReportType,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { Observable } from 'rxjs';
import { AnnualReportMetaInformationDTO } from '../dtos/annualReportMetaInformation.dto';
import { IDispatchAnnualReportsFilter } from '../dtos/dispatch-annual-reports-filter.dto';
import { IAuditLogDTO } from '../dtos/get-audit-log.dto';
import { PrintUsingPdfTronDto } from '../dtos/print-using-pdf-tron.dto';
import { DueDates } from '../interfaces/dueDate.interface';
import { AnnualReportExcelSheet } from '../interfaces/export-excel/annual-report-excel-sheet.interface';

export interface IAnnualReportProvider {
    // Seal/Unseal methods
    dispatchSealAnnualReport(annualReportId: string): void;
    dispatchUnsealAnnualReport(annualReportId: string): void;
    dispatchUpdatePrintOptions(annualReportEncryptedId: string, printOptions: IPrintOptions): void;
    dispatchGetReportSealHistory(annualReportEncryptedId: string): void;
    getReportSealHistory$(): Observable<IAuditLogDTO[]>;

    // Placeholder methods
    getPlaceholdersValues$(): Observable<IDynamicKeyValue>;
    dispatchUpdatePlaceholderUserData(userName: string): void;
    dispatchUpdatePlaceholderOrganizationData(organization: GetOrganizationDto): void;
    dispatchUpdateAopSnapshotPlaceholder(aopSnapshot: string): void;
    dispatchUpdateAopSnapshotCurrentYearPlaceholder(aopSnapshotCurrentYear: string): void;

    // Core CRUD methods
    createAnnualReport(
        setupData: ISetupData,
        existingAnnualReportEncryptedId?: string,
        existingProjectId?: string,
    ): void;
    dispatchUpdateAnnualReport(annualReport: IAnnualReport, annualReportEncryptedId: string): void;
    dispatchUpdateFinStatements(finStatements: IFinStatements[], annualReportEncryptedId: string): void;

    // Print and Export methods
    dispatchPrintAnnualReport(print: IAnnualReportPdfRequest): void;
    dispatchPrintAnnualReportUsingPdfTron(printUsingPdfTronRequestData: PrintUsingPdfTronDto): void;
    dispatchExportAnnualReportToWord(dataToExport: PrintUsingPdfTronDto): void;
    dispatchExportAnnualReportExcel(dataToExport: AnnualReportExcelSheet[]): void;
    dispatchExportAnnualReportExcelStructured(dataToExport: PrintUsingPdfTronDto): void;

    // Annual Report retrieval and management
    getActiveAnnualReport$(): Observable<IAnnualReport>;
    getAnnualReportIsSealed$(): Observable<boolean>;
    getActiveMetaInformation$(): Observable<AnnualReportMetaInformationDTO>;
    getMetaInformationByEncryptedId$(id: string): Observable<AnnualReportMetaInformationDTO>;
    dispatchGetAnnualReportToTakeNotesStructureFrom(encryptedId: string): void;
    getAnnualReportForUploadAndMappingAutoFill$(): Observable<IAnnualReport>;

    // Annual Report list methods
    dispatchActiveAnnualReportsList(filter?: IDispatchAnnualReportsFilter): void;
    dispatchAnnualReportsListToCopyFrom(): void;
    getAnnualReportsListToCopyFrom$(): Observable<AnnualReportMetaInformationDTO[]>;
    dispatchArchivedAnnualReportsList(filter?: IDispatchAnnualReportsFilter): void;
    getAnnualReports$(): Observable<AnnualReportMetaInformationDTO[]>;

    // Core dispatch methods
    dispatchActiveAnnualReport(annualReportId: string): void;
    dispatchBalanceAutoFillFromChosenOrSavedId(annualReportId: string): void;
    dispatchIncomeAutoFillFromChosenOrSavedId(annualReportId: string): void;

    // Reset methods
    resetAnnualReportForBalanceAutoFill(): void;
    resetAnnualReportForIncomeAutoFill(): void;
    resetActiveAnnualReport(): void;
    removeActiveAnnualReport(): void;

    // Previous annual report options
    dispatchGetPreviousAnnualReportOptions(
        companyId: string,
        currentAnnualReportId: string,
        legalFormId: string,
        accountingBasisId?: string,
        locale?: string,
    ): void;
    getPreviousAnnualReportOptions$(): Observable<Partial<IAnnualReport>[] | null>;

    // Company-related methods
    dispatchGetAnnualReportsByCompanyId(companyId: string): void;

    // AutoFill methods
    getAnnualReportForBalanceAutoFill$(): Observable<IAnnualReport>;
    getAnnualReportForIncomeAutoFill$(): Observable<IAnnualReport>;
    getAnnualReportForBalanceAutoFillFromCache$(): Observable<IAnnualReport | null>;
    getAnnualReportForIncomeAutoFillFromCache$(): Observable<IAnnualReport | null>;
    getActiveAnnualReportFromCache$(): Observable<IAnnualReport | null>;

    // State management
    setActiveAnnualReport(annualReport: IAnnualReport): void;
    setActiveAnnualReportEncryptedId(annualReportEncryptedId: string): void;
    getActiveAnnualReportEncryptedId$(): Observable<string>;

    // Financial Statement Sources
    setBalanceFinancialStatementSource(balanceFinancialStatementSource: IFinancialStatementSource): void;
    getBalanceFinancialStatementSource$(): Observable<IFinancialStatementSource>;
    setIncomeFinancialStatementSource(incomeFinancialStatementSource: IFinancialStatementSource): void;
    getIncomeFinancialStatementSource$(): Observable<IFinancialStatementSource>;

    // Archive/Restore/Delete methods
    dispatchArchiveAnnualReports(ids: string[]): void;
    dispatchRestoreAnnualReports(ids: string[]): void;
    dispatchDeleteAnnualReports(ids: string[]): void;

    // Utility methods
    isAnnualReportUrlIdValid$(id: string): Observable<boolean>;
    getError$(): Observable<HttpErrorResponse>;
    flush(): void;
    getFinancialStatmentImportLoseFocusChange$(): Observable<{} | undefined>;
    dispatchFinancialStatmentImportLoseFocus(): void;
    resetAnnualReports(): void;
    dispatchPrintView(printView: PrintUsingPdfTronDto): void;
    getPrintView$(): Observable<PrintUsingPdfTronDto>;

    // Financial Statements methods
    getActiveFinStatements$(): Observable<IFinStatements[]>;
    getBalanceAutofillFinStatements$(): Observable<IFinStatements[]>;
    getIncomeAutofillFinStatements$(): Observable<IFinStatements[]>;
    getSelectedYearForUploadAndMappingAutofillFinStatements$(): Observable<IFinStatements[]>;
    getActiveOriginalFinStatements$(): Observable<IFinStatements[]>;
    getFcpInputFinStatements$(): Observable<IFinStatements[]>;
    getActiveDueDates$(): Observable<DueDates | null>;

    // Additional methods
    dispatchGetAnnualReportForMappingAutoFill(encryptedId: string): void;
    getAnnualReportToCopyNotesStructureFrom$(): Observable<IAnnualReport>;
    dispatchGetNextBalanceSheetDateForCompanyById(companyId: string, reportType: ReportType): void;
}
